import classNames from "classnames";
import { ControllerStateAndHelpers, StateChangeOptions } from "downshift";
import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  AutoCompleteProps,
  AutoComplete
} from "@fidget/common/components/form/autoComplete/autoComplete";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ITagGroup } from "../../../models/tagGroups/TagGroupStore";
import { ITag } from "../../../models/tags/TagsStore";
import settingsStyles from "../../../styles/settings.css";
import { TagGroupPicker } from "./tagGroupPicker";
import { TagPicker } from "./tagPicker";

type PickerProps<T> = Pick<
  Partial<AutoCompleteProps<T>>,
  "className" | "initialIsOpen" | "hasError" | "onStateChange" | "onOutsideClick"
>;
interface Props extends PickerProps<ITagGroup | ITag> {
  onTagPicked(tag: ITag): void;
  onTagGroupPicked(tag: ITagGroup): void;
  /** Set tags suggested in autocomplete, if ommited will default to orgTags from TagsStore */
  providedTags?: ITag[];
  /** Set tag groups suggested in autocomplete, if ommited will default to tagGroups from TagGroupsStore */
  providedTagGroups?: ITagGroup[];
  textVariant?: TagTagGroupPickerTextVariant;

  onTypePickerClosed(): void;
}

export const TagTagGroupPicker = observer(
  ({
    providedTags,
    onTagPicked,
    providedTagGroups,
    onTagGroupPicked,
    className,
    onTypePickerClosed,
    textVariant = "choosing",
    ...autoCompleteProps
  }: Props) => {
    const [pickingType, setPickingType] = useState<Options>();

    const renderPicker = (type: Options) => {
      // note: any because we don't access items, just isOpen state
      const onStateChange = (_: StateChangeOptions<any>, state: ControllerStateAndHelpers<any>) => {
        if (!state.isOpen) {
          setPickingType(undefined);
        }
      };

      switch (type) {
        case Options.TagGroups:
          return (
            <TagGroupPicker
              {...autoCompleteProps}
              placeholder={mapOptionToString(textVariant, Options.TagGroups)}
              values={providedTagGroups}
              onChange={onTagGroupPicked}
              onStateChange={onStateChange}
            />
          );
        case Options.Tags:
          return (
            <TagPicker
              {...autoCompleteProps}
              placeholder={mapOptionToString(textVariant, Options.Tags)}
              values={providedTags}
              onChange={onTagPicked}
              onStateChange={onStateChange}
            />
          );
        default:
          return <></>;
      }
    };

    return (
      <div className={classNames(settingsStyles["input-max-width-bigger"], className)}>
        {pickingType ? (
          renderPicker(pickingType)
        ) : (
          <TypePicker
            textVariant={textVariant}
            onDiscard={onTypePickerClosed}
            onPicked={setPickingType}
          />
        )}
      </div>
    );
  }
);

enum Options {
  Tags = "Tags",
  TagGroups = "TagGroups"
}
export type TagTagGroupPickerTextVariant = "adding" | "removing" | "choosing";

interface TypePickerProps {
  onPicked(type: Options): void;
  onDiscard(): void;
  textVariant?: TagTagGroupPickerTextVariant;
}
const TypePicker = ({ onPicked, onDiscard, textVariant = "choosing" }: TypePickerProps) => {
  return (
    <AutoComplete<Options>
      itemsProvider={() => Object.values(Options)}
      itemToString={(option) => mapOptionToString(textVariant, option)}
      placeholder={getOptionPlaceholder(textVariant)}
      onChange={(val: Options | "") => (val ? onPicked(val) : undefined)}
      initialIsOpen
      readOnly
      onStateChange={(_, state) => (!state.selectedItem && !state.isOpen ? onDiscard() : undefined)}
    />
  );
};

const mapOptionToString = (textVariant: TagTagGroupPickerTextVariant, item: Options | null) => {
  switch (item) {
    case Options.Tags:
      switch (textVariant) {
        case "adding":
          return intl.formatMessage("tagtagGroupPicker.tags.add");
        case "removing":
          return intl.formatMessage("tagtagGroupPicker.tags.remove");
        default:
          return intl.formatMessage("tagtagGroupPicker.tags");
      }
    case Options.TagGroups:
      switch (textVariant) {
        case "adding":
          return intl.formatMessage("tagtagGroupPicker.tagGroups.add");
        case "removing":
          return intl.formatMessage("tagtagGroupPicker.tagGroups.remove");
        default:
          return intl.formatMessage("tagtagGroupPicker.tagGroups");
      }
    default:
      return "";
  }
};

const getOptionPlaceholder = (variant: TagTagGroupPickerTextVariant) => {
  switch (variant) {
    case "adding":
      return intl.formatMessage("tagtagGroupPicker.placeholder.add");
    case "removing":
      return intl.formatMessage("tagtagGroupPicker.placeholder.remove");
    default:
      return intl.formatMessage("tagtagGroupPicker.placeholder");
  }
};
